import { useEffect, useEffectEvent } from "react";
import type { NativeFocusSupport } from "./nativeCamera";
import { useNativeCameraSnapshot } from "./useNativeCamera";

const SETTLED_VISIBLE_MS = 600;
// The wrapper promises `focused` within 1.5 s; a ring left pulsing past that would mislead.
const ADJUSTING_TIMEOUT_MS = 2000;

type FocusRingProps = {
	/** Tap position in px inside the element the ring is positioned in. */
	x: number;
	y: number;
	/** `exposure` draws the lighter indicator for a fixed-focus camera that only re-meters. */
	mode: Exclude<NativeFocusSupport, "none">;
	onDone: () => void;
};

/** The focus ring drawn at a tap on the native preview; follows the wrapper's `camera.focus` phases. */
export function FocusRing({ x, y, mode, onDone }: FocusRingProps) {
	const phase = useNativeCameraSnapshot().focus?.phase ?? "adjusting";
	const finish = useEffectEvent(onDone);

	useEffect(() => {
		if (phase === "reset" || phase === "failed") {
			finish();
			return;
		}
		const timer = window.setTimeout(finish, phase === "focused" ? SETTLED_VISIBLE_MS : ADJUSTING_TIMEOUT_MS);
		return () => window.clearTimeout(timer);
	}, [phase]);

	const size = mode === "focus" ? 72 : 44;
	const tone = phase === "focused" ? "border-lime-400" : mode === "focus" ? "border-white" : "border-white/60";

	return (
		<div
			aria-hidden
			data-native-focus-ring={phase}
			className={`pointer-events-none absolute z-20 rounded-full border-2 ${tone} ${
				phase === "adjusting" ? "native-focus-ring-adjusting" : ""
			}`}
			style={{ left: x - size / 2, top: y - size / 2, width: size, height: size }}
		/>
	);
}
